


































































































































































































































































































































































































































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";

// Providers
import TldProvider from "@/modules/web_hosting/components/domains/tldProvider.vue";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DeferModes } from "@/data/enums/provisioning";
import { RegistrarTypes } from "@/modules/web_hosting/data/enums";

// Types
import { ITldForm } from "@/modules/web_hosting/models/domains";
import { IProvisionProvider, IProvisionCategory } from "@/models/provisioning";
import { IProduct, IProductPriceRecalculation } from "@/models/products";

@Component({
  components: {
    "tld-provider": TldProvider,
    "invoice-generation-control": () =>
      import("@/components/app/global/invoices/invoiceGenerationControl.vue"),
    "recurring-due-date-control": () =>
      import("@/components/app/global/invoices/recurringDueDateControl.vue"),
    "tld-pricing": () =>
      import("@/modules/web_hosting/components/domains/tldPricing.vue"),
    "provision-defer-mode-field": () =>
      import(
        "@/components/app/admin/catalogue/categories/provisionDeferModeField.vue"
      )
  }
})
export default class ManageTldModal extends Mixins(
  HasValidation,
  RequestPromises
) {
  // Refs

  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    tld: HTMLFormElement;
    tldProvider: TldProvider;
  };

  // Props

  @Prop({ default: null })
  public tld!: IProduct;

  @Prop({ required: true })
  public domainProviders!: IProvisionProvider[];

  @Prop({ required: true })
  public domainCategoryId!: IProvisionCategory["id"];

  // Variables

  public hasPriceErrors = false;
  public isLoadingSettings = false;
  public isReloadingSettings = false;
  public hasSettingsError = false;

  public form: ITldForm = {
    tld: "",
    enabled: true,
    provision_configuration_id: null,
    create_invoice_term: null,
    payment_days_term: null,
    late_renew_days: "",
    max_reg_period_years: "",
    redemption_period_days: "",
    prices: [],
    additional_currency_recalculation: 0,
    provision_setup_field_defer_mode: DeferModes.NONE
  };
  public initialForm = {} as ITldForm;

  public isProcessing = false;
  public enableProvisioning = true;

  public isNextStep = false;
  public adminTldsRouteName = "admin__hostControl::domainTlds";
  public recalculation = {} as IProductPriceRecalculation;

  // Methods

  public initiate() {
    // Create new TLD
    if (!this.isUpdate) {
      this.tldFocus();
      this.updateRecalculation();
      return;
    }

    // Update TLD
    this.isNextStep = true;
    this.initialiseForm();
  }

  public initialiseForm(forceReload = false) {
    if (forceReload || !this.isUpdate) {
      _.forEach(
        this.$refs.tldProvider.defaultTldSettings,
        (defaultTldSettingValue, defaultTldSettingKey) => {
          if (!_.isNull(defaultTldSettingValue))
            this.$set(this.form, defaultTldSettingKey, defaultTldSettingValue);
        }
      );
    } else {
      this.form = _.merge(
        this.form,
        _.pick(this.tld, [
          "tld",
          "enabled",
          "create_invoice_term",
          "late_renew_days",
          "max_reg_period_years",
          "payment_days_term",
          "redemption_period_days",
          "additional_currency_recalculation",
          "provision_setup_field_defer_mode",
          "provision_configuration_id"
        ]),
        {
          prices: _.map(
            _.filter(this.tld.prices, [
              "pricelist_id",
              this.$refs.tldProvider.brandPriceListId
            ]),
            tldPrice =>
              _.pick(tldPrice, [
                "billing_cycle_months",
                "price",
                "pricelist_id",
                "currency_id"
              ])
          )
        }
      );

      this.enableProvisioning = !_.isNull(this.tld.provision_blueprint_id);

      if (this.enableProvisioning && this.isManual) {
        this.$set(
          this.form,
          "provision_configuration_id",
          RegistrarTypes.MANUAL
        );
      }
    }

    this.updateRecalculation();

    this.initialForm = _.cloneDeep(this.form);
  }

  public updateRecalculation() {
    this.recalculation = _.find(
      this.$refs.tldProvider?.multicurrencyOptions,
      option =>
        option.value ===
        _.get(this.form, "additional_currency_recalculation", 0)
    ) as IProductPriceRecalculation;
  }

  public changeRecalculation(newRecalculation) {
    _.set(
      this.form,
      "additional_currency_recalculation",
      newRecalculation ? newRecalculation.value : 0
    );

    if (!newRecalculation) {
      this.recalculation = this.$refs.tldProvider?.multicurrencyOptions[0];
    }
  }

  public async loadDefaultTldSettings() {
    try {
      await this.$refs.tldProvider?.getTldDefaultSettings(this.form.tld);
      this.initialiseForm(true);
      this.hasSettingsError = false;
    } catch (error) {
      this.hasSettingsError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoadingSettings = false;
      this.isReloadingSettings = false;
    }
  }

  public reloadSettingsData() {
    this.isReloadingSettings = true;
    this.loadDefaultTldSettings();
  }

  public async validateBeforeSubmit() {
    try {
      // Creating new - first step
      if (!this.isNextStep) {
        this.$set(this, "isNextStep", true);
        this.isLoadingSettings = true;
        this.loadDefaultTldSettings();
        return;
      }

      // Creating new - second step
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      this.isProcessing = true;

      this.isUpdate
        ? await this.$refs.tldProvider?.updateTld(this.formValues)
        : await this.$refs.tldProvider?.createTld(this.formValues);

      if (this.$route.name !== this.adminTldsRouteName) {
        this.$router.push({
          name: this.adminTldsRouteName
        });
      }

      this.$store.dispatch("toast/show", {
        message: this.isUpdate
          ? this.$t("_modules.web_hosting.domains.tld_successfully_updated")
          : this.$t("_modules.web_hosting.domains.tld_successfully_created")
      });

      this.$emit("success");
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public changeTld() {
    this.isNextStep = false;
    this.$refs.tld.focus();
  }

  public tldFocus() {
    setTimeout(() => {
      this.$nextTick(() => {
        this.$refs.tld?.focus();
      });
    }, 50);
  }

  // Computed

  public get isUpdate() {
    return !_.isNull(this.tld);
  }

  public get isManual() {
    return (
      this.tld.provision_blueprint_id ===
      this.$refs.tldProvider.manualBlueprint.id
    );
  }

  public get tldId() {
    return this.tld?.id ?? null;
  }

  public get title() {
    if (!this.isUpdate)
      return this.$t("_modules.web_hosting.domains.add_new_tld");
    return this.$t("_modules.web_hosting.domains.update_tld");
  }

  public get canShowNextStepFields() {
    return (
      this.isNextStep &&
      !this.isLoadingSettings &&
      !this.isReloadingSettings &&
      !this.hasSettingsError
    );
  }

  public get mainButtonPath() {
    if (!this.isNextStep) return "_action.next";

    return this.isUpdate ? "_action.update" : "_action.create";
  }

  public get formIsValid() {
    return (
      !this.isProcessing &&
      !this.hasErrors &&
      !this.hasSettingsError &&
      !!this.form.tld.length &&
      !this.hasPriceErrors
    );
  }

  public get formValues() {
    const formValues = _.omitBy(
      this.form,
      formValue =>
        formValue === "" || (_.isArray(formValue) && formValue.length === 0)
    );

    if (formValues.provision_configuration_id === RegistrarTypes.MANUAL) {
      formValues.provision_configuration_id = "";
      formValues[
        "provision_blueprint_id"
      ] = this.$refs.tldProvider?.manualBlueprint.id;
    }

    if (!this.enableProvisioning) {
      if (_.has(formValues, "provision_blueprint_id"))
        formValues["provision_blueprint_id"] = null;
      formValues.provision_configuration_id = null;
    }

    return formValues;
  }

  public get deferModeCV() {
    return _.get(this.tld, "category.provision_setup_field_defer_mode", null);
  }

  // Guarding

  public get canManagePrices() {
    const managePricesFunctionalities = [
      "create_pricelist_product_prices",
      "delete_pricelist_product_prices",
      "sync_pricelist_product_prices",
      "update_product"
    ];
    return this.$userCan(...managePricesFunctionalities);
  }
}
